import PropTypes from "prop-types";
import React from "react";
import ReactOnRails from "react-on-rails";
import ReCAPTCHA from "react-google-recaptcha";

import Loading from "../Loading";
import * as api from "../../utils/api";

const GENERIC_ERROR = "An error occurred, please double check your info and try again.";

// axios rejects on 409/422, so the messages that matter most -- "you already
// have an account, try logging in" and the specific validation failure -- only
// ever reach the .catch. They used to be replaced there by the generic string.
function serverError(e) {
  return (e && e.response && e.response.data && e.response.data.error) || GENERIC_ERROR;
}

export default class SignupForm extends React.Component {
  static propTypes = {
    form_authenticity_token: PropTypes.string,
    discord_signup_url: PropTypes.string,
    step: PropTypes.number,
    currentUserId: PropTypes.number,
    gameId: PropTypes.number,
    activateGroupMind: PropTypes.bool,
    groupSignup: PropTypes.bool,
    captchaKey: PropTypes.string,
  };

  state = {
    platformsList: [
      { label: "PC", value: "pc" },
      { label: "PS5", value: "ps5" },
      { label: "PS4", value: "ps4" },
      { label: "Xbox Series X|S", value: "xbox-series" },
      { label: "Xbox One", value: "xbox-one" },
      { label: "Switch", value: "nintendo-switch" },
    ],
    platform: "",
    ageRange: "",
    playSchedule: "",
    playScheduleList: [
      "Weekday Mornings and Weekends",
      "Weekday Afternoons and Weekends",
      "Weekday Evenings and Weekends",
      "Weekdays Latenight and Weekends",
    ],
    interestsList: [],
    interests: [],
    firstName: "",
    email: "",
    password: "",
    termsCheckbox: false,
    step: this.props.step || 0,
    errors: "",
    loading: false,
    captchaToken: "",
  };

  componentDidMount() {
    // this.fetchAllTags()
    this.setState({ loading: false });
    if (this.props.currentUserId && this.state.step == 1 && newTimeZone) {
      api.updateUser({
        headers: ReactOnRails.authenticityHeaders(),
        body: {
          user_id: this.props.currentUserId,
          time_zone: newTimeZone,
        },
      });
    }
  }

  fetchAllTags = () => {
    this.setState({ loading: true });

    api.fetchAllGroupTags({ suggestedOnly: true }).then((response) => {
      this.setState({ interestsList: response, loading: false });
    });
  };

  onChange = (value) => {
    this.setState({ captchaToken: value });
  };

  // A Discord signup already exists as an account by the time it reaches step 1
  // (the OAuth callback creates it), and every answer used to be written in one
  // shot after step 4 — so abandoning mid-wizard left a permanent row with no
  // platform, schedule or age, which matchmaking can never place. Save each
  // answer as it's given. Fire-and-forget: the final submit re-sends the whole
  // profile, so a failed step save must never block the wizard.
  saveStep = (fields) => {
    if (!this.props.currentUserId) {
      return;
    }

    api
      .updateUser({
        headers: ReactOnRails.authenticityHeaders(),
        body: { user_id: this.props.currentUserId, ...fields },
      })
      .catch(() => {
        // Fire-and-forget (see above): the final submit re-sends everything.
      });
  };

  updatePlatform = (platform) => {
    this.saveStep({ platform });
    this.setState({ platform, step: 2 });
  };

  updatePlaySchedule = (playSchedule) => {
    this.saveStep({ play_schedule: playSchedule });
    this.setState({ playSchedule, step: 3 });
  };

  updateAge = (ageRange) => {
    this.saveStep({ age: ageRange, hide_age: true });
    this.fetchAllTags();
    this.setState({ ageRange, step: 4 });
  };

  updateInterests = (interest) => {
    this.state.interests.includes(interest)
      ? this.setState({ interests: this.state.interests.filter((i) => i !== interest) })
      : this.setState({ interests: [...this.state.interests, interest] });
  };

  checkIfLoggedIn = () => {
    if (this.props.currentUserId) {
      this.setState({ loading: true });

      api
        .updateUser({
          headers: ReactOnRails.authenticityHeaders(),
          body: {
            user_id: this.props.currentUserId,
            age: this.state.ageRange,
            hide_age: true,
            platform: this.state.platform,
            play_schedule: this.state.playSchedule,
            play_style: this.state.interests.includes("competitive") ? "serious" : "casual",
            time_zone: newTimeZone,
            tag_list: this.state.interests,
            tos_privacy_agreement: true,
            primary_game_id: this.props.gameId,
            activate_group_mind: this.props.activateGroupMind,
          },
        })
        .then((response) => {
          if (response.data.redirect_url) {
            let redirect = window.encodeURI(response.data.redirect_url);
            window.location.href = redirect;
          } else {
            this.setState({ loading: false, termsCheckbox: false, errors: response.data.error });
          }
        })
        .catch((e) => {
          this.setState({ loading: false, termsCheckbox: false, errors: serverError(e) });
        });
    } else {
      this.setState({ step: 5 });
    }
  };

  updateGamertag = (gamertag) => {
    this.setState({ gamertag });
  };

  updateEmail = (email) => {
    this.setState({ email });
  };

  updatePassword = (password) => {
    this.setState({ password });
  };

  handleCheckbox = () => {
    this.setState({ termsCheckbox: !this.state.termsCheckbox });
  };

  formReady = () => {
    return this.state.gamertag && this.state.email && this.state.password && this.state.termsCheckbox;
  };

  onSubmit = () => {
    this.setState({ loading: true });

    api
      .createUser({
        headers: ReactOnRails.authenticityHeaders(),
        body: {
          gamertag: this.state.gamertag,
          email: this.state.email,
          password: this.state.password,
          age: this.state.ageRange,
          hide_age: true,
          platform: this.state.platform,
          play_schedule: this.state.playSchedule,
          play_style: this.state.interests.includes("competitive") ? "serious" : "casual",
          time_zone: newTimeZone,
          tag_list: this.state.interests,
          tos_privacy_agreement: true,
          primary_game_id: this.props.gameId,
          activate_group_mind: this.props.activateGroupMind,
          captcha_token: this.state.captchaToken,
          created_from: "website",
          // Not a column: tells the server this signup came from the "add your
          // clan" funnel, which really does want /groups/new?step=5 next.
          group_signup: this.props.groupSignup ? true : false,
        },
      })
      .then((response) => {
        if (response.data.redirect_url) {
          let redirect = window.encodeURI(response.data.redirect_url);
          window.location.href = redirect;
        } else {
          this.setState({ loading: false, termsCheckbox: false, errors: response.data.error });
        }
      })
      .catch((e) => {
        this.setState({ loading: false, termsCheckbox: false, errors: serverError(e) });
      });
  };

  render() {
    if (this.state.loading) {
      return (
        <div>
          {this.props.groupSignup ? (
            <h3>Setting up your account…</h3>
          ) : (
            <p className="lead">Finding your group…</p>
          )}
          <h3></h3>
          <br />
          <div className="col-10 col-md-12 col-xl-10 gap-xy-2">
            <div style={{ minHeight: "300px" }}>
              <Loading />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        {this.state.step == 0 && (
          <div>
            {this.props.groupSignup ? (
              <h3>First, Create Your Account:</h3>
            ) : this.props.guildId ? (
              <div>
                <h3>Join {this.props.groupName} To Schedule Games:</h3>
              </div>
            ) : (
              <h3>Create Your Account</h3>
            )}

            <br />
            <div className="col-10 col-md-12 col-xl-10 gap-xy-2">
              <a href={this.props.discord_signup_url} className="btn btn-primary btn-lg">
                Sign Up With Discord
              </a>
              {this.props.groupSignup ? (
                <a href={"/groups/new?step=1"} className="btn btn-primary btn-lg">
                  Sign Up With Email
                </a>
              ) : this.props.guildId ? null : (
                <button type="button" className="btn btn-primary btn-lg" onClick={() => this.setState({ step: 1 })}>
                  {" "}
                  Sign Up With Email
                </button>
              )}
            </div>
          </div>
        )}

        {this.state.step == 1 && (
          <div>
            <p className="lead" data-font="Lato:700">
              {"looks like you're in the " + newTimeZone + " timezone."}
            </p>
            {this.props.groupSignup ? (
              <h3>What platform does your group mostly play on?</h3>
            ) : (
              <h3>What platform do you mostly play on?</h3>
            )}
            <br />
            <div className="col-10 col-md-12 col-xl-10 gap-xy-2">
              {this.state.platformsList.map((platform) => (
                <button
                  type="button"
                  key={platform.value}
                  className={"btn " + platform.value}
                  onClick={() => this.updatePlatform(platform.value)}
                >
                  {platform.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {this.state.step == 2 && (
          <div>
            {this.props.groupSignup ? <h3>When does your group usually play?</h3> : <h3>When do you usually play?</h3>}
            <br />
            <div className="col-10 col-md-12 col-xl-10 gap-xy-2">
              {this.state.playScheduleList.map((playSchedule) => (
                <button
                  type="button"
                  className="btn btn-glass btn-dark"
                  onClick={() => this.updatePlaySchedule(playSchedule)}
                >
                  {playSchedule}
                </button>
              ))}
            </div>
            <br />
            <div className="d-inline">
              <button
                type="button"
                className="btn btn-disabled pull-left"
                onClick={() => this.setState({ step: 1, playSchedule: "" })}
              >
                <i className="fa fa-chevron-left"></i>
              </button>
            </div>
          </div>
        )}

        {this.state.step == 3 && (
          <div>
            {this.props.groupSignup ? (
              <h3>What's your group's rough age range?</h3>
            ) : (
              <h3>What's your rough age range?</h3>
            )}
            <br />
            <div className="col-10 col-md-12 col-xl-10 gap-xy-2">
              <button type="button" className="btn btn-glass btn-dark" onClick={() => this.updateAge("15")}>
                teens
              </button>
              <button type="button" className="btn btn-glass btn-dark" onClick={() => this.updateAge("25")}>
                twenties{" "}
              </button>
              <button type="button" className="btn btn-glass btn-dark" onClick={() => this.updateAge("35")}>
                thirties
              </button>
              <button type="button" className="btn btn-glass btn-dark" onClick={() => this.updateAge("45")}>
                forties
              </button>
              <button type="button" className="btn btn-glass btn-dark" onClick={() => this.updateAge("55")}>
                fifties
              </button>
              <button type="button" className="btn btn-glass btn-dark" onClick={() => this.updateAge("65")}>
                sixties
              </button>
              <button type="button" className="btn btn-glass btn-dark" onClick={() => this.updateAge("75")}>
                seventies +
              </button>
            </div>
            <br />
            <div className="d-inline">
              <button
                type="button"
                className="btn btn-disabled pull-left"
                onClick={() => this.setState({ step: 2, ageRange: "" })}
              >
                <i className="fa fa-chevron-left"></i>
              </button>
            </div>
          </div>
        )}

        {this.state.step == 4 && (
          <div>
            {this.props.groupSignup ? (
              <h3>Pick a few tags to describe your group (optional...):</h3>
            ) : (
              <h3>What tags describe you? Pick a few:</h3>
            )}
            <br />
            <div className="col-10 col-md-12 col-xl-10 gap-xy-2">
              {this.state.interestsList.map((interest) => (
                <button
                  type="button"
                  className={"btn btn-glass " + (this.state.interests.includes(interest) ? "btn-primary" : "btn-dark")}
                  onClick={() => this.updateInterests(interest)}
                >
                  {interest}
                </button>
              ))}
            </div>
            <br />
            <br />
            <div className="col-10 col-md-12 col-xl-10">
              <div className="d-inline">
                <button
                  type="button"
                  className="btn btn-disabled pull-left"
                  onClick={() => this.setState({ step: 3, interests: [] })}
                >
                  <i className="fa fa-chevron-left"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-lg pull-right"
                  onClick={() => this.checkIfLoggedIn()}
                >
                  next
                </button>
              </div>
            </div>
          </div>
        )}

        {this.state.step == 5 && (
          <div>
            {this.props.groupSignup ? (
              <h3>Almost done! Finish signup to join:</h3>
            ) : (
              <h3>We found your group! Finish signup to join:</h3>
            )}
            {this.state.errors && (
              <div className="alert alert-danger mt-3" role="alert">
                {this.state.errors}
              </div>
            )}
            <br />
            <div className="col-8 col-md-8 col-xl-6">
              <form className="input-transparent">
                <div className="form-group input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Username"
                    aria-label="Username"
                    value={this.state.gamertag}
                    onChange={(e) => this.updateGamertag(e.target.value)}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">
                      <i className="fa fa-user"></i>
                    </span>
                  </div>
                </div>

                <div className="form-group input-group">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Email address"
                    aria-label="Email address"
                    value={this.state.email}
                    onChange={(e) => this.updateEmail(e.target.value)}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">
                      <i className="fa fa-envelope"></i>
                    </span>
                  </div>
                </div>

                <div className="form-group input-group">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Choose a password"
                    aria-label="Choose a password"
                    value={this.state.password}
                    onChange={(e) => this.updatePassword(e.target.value)}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">
                      <i className="fa fa-lock"></i>
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      id="tos-agree"
                      className="custom-control-input"
                      checked={this.state.termsCheckbox}
                      onChange={() => this.handleCheckbox()}
                    />
                    <label className="custom-control-label" htmlFor="tos-agree">
                      I agree to the Terms of Service and Privacy Policy
                    </label>
                  </div>
                  <span className="small-2 text-muted">
                    Read the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-muted">
                      Terms of Service
                    </a>{" "}
                    &amp;{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-muted">
                      Privacy Policy
                    </a>
                  </span>
                </div>

                <div className="form-group">
                  <ReCAPTCHA sitekey={this.props.captchaKey} onChange={this.onChange} theme="dark" />
                </div>

                <div className="d-inline">
                  <button
                    type="button"
                    className="btn btn-disabled pull-left"
                    onClick={() => this.setState({ step: 4, interests: [] })}
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                  {this.formReady() ? (
                    <button type="button" className="btn btn-primary pull-right" onClick={() => this.onSubmit()}>
                      Create Account
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary pull-right"
                      disabled
                      title="Fill in a username, email, and password, and accept the terms to continue."
                    >
                      Create Account
                    </button>
                  )}
                </div>
                {!this.formReady() && (
                  <p className="small-2 text-muted mt-3">
                    Add a username, email, and password, and accept the terms to create your account.
                  </p>
                )}
              </form>
            </div>
            <br /> <br />
          </div>
        )}
      </div>
    );
  }
}
